/* ==========================================================================
   Sushi Push Pop — order drawer.

   Vanilla JS, no dependencies, same shape as assets/jaduzar-help.js: nothing
   here knows a variant id or a price. Both come from the JSON block that
   snippets/spp-data.liquid renders out of the real Shopify product, so a
   price shown in the drawer is by construction the price the cart charges.

   The package total is variant.price. The unit price is that same number
   divided by the units in the package, formatted to three decimals — it is
   never a second, independently stored figure that could drift.
   ========================================================================== */
(function () {
  'use strict';

  var STEP_COUNT = 4;
  var MAX_FILE_BYTES = 20 * 1024 * 1024;
  var ALLOWED_EXT = ['png', 'jpg', 'jpeg', 'svg', 'pdf'];
  var PREVIEWABLE = ['png', 'jpg', 'jpeg', 'svg'];

  var STRINGS = {
    stepOf: 'Step SPP_STEP of SPP_TOTAL',
    position: 'SPP_INDEX of SPP_TOTAL',
    perUnit: 'per unit',
    viewList: 'View full price list',
    hideList: 'Hide full price list',
    quantity: 'Quantity',
    unitPrice: 'Unit price',
    productTotal: 'Product total',
    yes: 'Yes',
    no: 'No',
    none: 'Not provided',
    onePackage: '1 package',
    errQuality: 'Choose a quality option to continue.',
    errVariant: 'This package is not available right now. Choose another quantity or contact us.',
    errFile: 'Upload your logo file to continue.',
    errAck: 'Please confirm that shipping/freight is not included.',
    errFileType: 'Use a PNG, JPG/JPEG, SVG or PDF file.',
    errFileSize: 'That file is larger than 20 MB. Use a smaller file.',
    errCart: 'The package could not be added. Please try again.',
    adding: 'Adding to cart…'
  };

  function all(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  /* Placeholders are plain tokens rather than {braces}: a brace inside a
     Liquid string literal breaks Shopify's parser, so the locale strings
     arrive carrying SPP_STEP / SPP_INDEX / SPP_TOTAL instead. */
  function fill(template, values) {
    var text = String(template);
    Object.keys(values).forEach(function (token) {
      text = text.split(token).join(values[token]);
    });
    return text;
  }

  function groupDigits(value) {
    return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function formatBytes(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  function extensionOf(name) {
    var parts = String(name).toLowerCase().split('.');
    return parts.length > 1 ? parts.pop() : '';
  }

  /* ---------------------------------------------------------------------
     Money. The shop's own format is read out of a rendered sample rather
     than assumed, so a store that writes 1.234,56 keeps writing it that way
     and dollars are never quietly relabelled.
     --------------------------------------------------------------------- */
  function moneyShape(sample) {
    var shape = { prefix: '', suffix: '', decimal: '.', group: ',' };
    if (!sample) return shape;

    var first = sample.search(/\d/);
    var last = sample.length - 1 - String(sample).split('').reverse().join('').search(/\d/);
    if (first < 0) return shape;

    shape.prefix = sample.slice(0, first);
    shape.suffix = sample.slice(last + 1);

    var core = sample.slice(first, last + 1);
    var tail = core.match(/([.,])(\d{2})$/);
    if (tail) {
      shape.decimal = tail[1];
      shape.group = tail[1] === '.' ? ',' : '.';
    } else if (core.indexOf(',') > -1) {
      shape.group = ',';
    } else if (core.indexOf('.') > -1) {
      shape.group = '.';
    }
    return shape;
  }

  /* Unit price, three decimals, derived from the variant price in cents. */
  function unitPrice(shape, cents, units) {
    if (!units) return '';
    var value = cents / 100 / units;
    var text = value.toFixed(3);
    var parts = text.split('.');
    var whole = String(parts[0]).replace(/\B(?=(\d{3})+(?!\d))/g, shape.group);
    return shape.prefix + whole + shape.decimal + parts[1] + shape.suffix;
  }

  function readData() {
    var node = document.querySelector('[data-spp-data]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function init() {
    var root = document.querySelector('[data-spp-root]');
    if (!root || root.getAttribute('data-spp-ready') === 'true') return;

    var data = readData();
    if (!data || !data.ready || !data.variants || !data.variants.length) return;

    root.setAttribute('data-spp-ready', 'true');

    /* Every visible word comes from the theme's locale file, so it can be
       changed under Actions › Edit default theme content. The literals above
       are only a fallback for a locale that is missing a key. */
    if (data.strings) {
      Object.keys(data.strings).forEach(function (key) {
        if (data.strings[key]) STRINGS[key] = data.strings[key];
      });
    }

    /* Out of the section wrapper and onto <body>: position:fixed must not be
       trapped by a transformed or clipped ancestor. */
    if (root.parentNode !== document.body) document.body.appendChild(root);

    var shape = moneyShape(data.moneySample);

    var form = root.querySelector('[data-spp-form]');
    var panel = root.querySelector('[data-spp-panel]');
    var scrim = root.querySelector('[data-spp-scrim]');
    var body = root.querySelector('[data-spp-body]');
    var steps = all(root, '[data-spp-step]');
    var bars = all(root, '[data-spp-bar]');
    var stepline = root.querySelector('[data-spp-stepline]');
    var stepTitle = root.querySelector('[data-spp-step-title]');
    var prevBtn = root.querySelector('[data-spp-prev]');
    var nextBtn = root.querySelector('[data-spp-next]');
    var submitBtn = root.querySelector('[data-spp-submit]');
    var submitLabel = root.querySelector('[data-spp-submit-label]');

    var variantInput = root.querySelector('[data-spp-variant]');
    var propQuality = root.querySelector('[data-spp-prop-quality]');
    var propUnits = root.querySelector('[data-spp-prop-units]');
    var propLogo = root.querySelector('[data-spp-prop-logo]');

    var qualityInputs = all(root, '[data-spp-quality]');
    var qtyOut = root.querySelector('[data-spp-qty]');
    var qtyPos = root.querySelector('[data-spp-qty-pos]');
    var prevQty = root.querySelector('[data-spp-prev-qty]');
    var nextQty = root.querySelector('[data-spp-next-qty]');
    var stepper = root.querySelector('[data-spp-stepper]');
    var unitOut = root.querySelector('[data-spp-unit-price]');
    var totalOut = root.querySelector('[data-spp-total]');

    var tableToggle = root.querySelector('[data-spp-table-toggle]');
    var tableLabel = root.querySelector('[data-spp-table-label]');
    var tableWrap = root.querySelector('[data-spp-table]');

    var logoChoices = all(root, '[data-spp-logo-choice]');
    var uploadBlock = root.querySelector('[data-spp-upload]');
    var fileInput = root.querySelector('[data-spp-file]');
    var dropZone = root.querySelector('[data-spp-drop]');
    var fileCard = root.querySelector('[data-spp-file-card]');
    var filePreview = root.querySelector('[data-spp-file-preview]');
    var fileName = root.querySelector('[data-spp-file-name]');
    var fileSize = root.querySelector('[data-spp-file-size]');
    var fileReplace = root.querySelector('[data-spp-file-replace]');
    var fileRemove = root.querySelector('[data-spp-file-remove]');
    var fileError = root.querySelector('[data-spp-file-error]');
    var fileProgress = root.querySelector('[data-spp-file-progress]');
    var ackInput = root.querySelector('[data-spp-ack]');
    var brandInput = root.querySelector('#spp-brand');
    var countryInput = root.querySelector('#spp-country');
    var cityInput = root.querySelector('#spp-city');
    var freightInputs = all(root, '[data-spp-freight]');

    if (!form || !panel || steps.length !== STEP_COUNT) return;

    var current = 1;
    var quality = '';
    var levelIndex = 0;
    var busy = false;
    var opener = null;
    var previewUrl = null;

    /* ------------------------------------------------------------------
       Variant index — quality + units resolve to exactly one real variant
       ------------------------------------------------------------------ */
    var byQuality = { premium: {}, standard: {} };
    data.variants.forEach(function (variant) {
      if (!byQuality[variant.quality]) byQuality[variant.quality] = {};
      byQuality[variant.quality][variant.units] = variant;
    });

    function levelsFor(qualityId) {
      var table = byQuality[qualityId] || {};
      return data.levels.filter(function (level) {
        return Object.prototype.hasOwnProperty.call(table, level);
      });
    }

    function currentLevels() {
      return levelsFor(quality || 'premium');
    }

    function currentVariant() {
      var levels = currentLevels();
      var level = levels[levelIndex];
      if (!quality || level === undefined) return null;
      return byQuality[quality][level] || null;
    }

    function qualityLabel(qualityId) {
      var match = (data.qualities || []).filter(function (entry) {
        return entry.id === qualityId;
      })[0];
      return match ? match.label : qualityId;
    }

    /* ------------------------------------------------------------------
       Rendering
       ------------------------------------------------------------------ */
    function renderStepper() {
      var levels = currentLevels();
      if (!levels.length) return;
      if (levelIndex > levels.length - 1) levelIndex = levels.length - 1;
      if (levelIndex < 0) levelIndex = 0;

      var level = levels[levelIndex];
      var variant = currentVariant();

      if (qtyOut) {
        qtyOut.textContent = groupDigits(level);
        qtyOut.classList.remove('is-swapping');
        /* Restart the animation without waiting a frame we cannot rely on. */
        void qtyOut.offsetWidth;
        qtyOut.classList.add('is-swapping');
      }
      if (qtyPos) {
        qtyPos.textContent = fill(STRINGS.position, { SPP_INDEX: levelIndex + 1, SPP_TOTAL: levels.length });
      }
      if (prevQty) prevQty.disabled = levelIndex === 0;
      if (nextQty) nextQty.disabled = levelIndex === levels.length - 1;

      if (variant) {
        if (unitOut) unitOut.textContent = unitPrice(shape, variant.price, variant.units);
        if (totalOut) totalOut.textContent = variant.moneyLong || variant.money;
        if (variantInput) variantInput.value = variant.id;
        if (propQuality) propQuality.value = variant.optionQuality || qualityLabel(variant.quality);
        if (propUnits) propUnits.value = variant.optionPackage || groupDigits(variant.units) + ' units';
      }

      markTableRow();
      clearError(2);
      if (variant && !variant.available) showError(2, STRINGS.errVariant);
    }

    function buildTable() {
      if (!tableWrap || !quality) return;
      var levels = currentLevels();
      var table = document.createElement('table');
      table.className = 'spp-table';

      var caption = document.createElement('caption');
      caption.textContent = qualityLabel(quality) + ' · ' + (data.currency || '');
      table.appendChild(caption);

      var head = document.createElement('thead');
      var headRow = document.createElement('tr');
      [STRINGS.quantity, STRINGS.unitPrice, STRINGS.productTotal].forEach(function (label) {
        var cell = document.createElement('th');
        cell.setAttribute('scope', 'col');
        cell.textContent = label;
        headRow.appendChild(cell);
      });
      head.appendChild(headRow);
      table.appendChild(head);

      var tbody = document.createElement('tbody');
      levels.forEach(function (level) {
        var variant = byQuality[quality][level];
        var row = document.createElement('tr');
        row.setAttribute('data-spp-row', String(level));

        var qtyCell = document.createElement('th');
        qtyCell.setAttribute('scope', 'row');
        qtyCell.textContent = groupDigits(level);
        row.appendChild(qtyCell);

        var unitCell = document.createElement('td');
        unitCell.textContent = unitPrice(shape, variant.price, variant.units);
        row.appendChild(unitCell);

        var totalCell = document.createElement('td');
        totalCell.textContent = variant.money;
        row.appendChild(totalCell);

        tbody.appendChild(row);
      });
      table.appendChild(tbody);

      tableWrap.textContent = '';
      tableWrap.appendChild(table);
      markTableRow();
    }

    function markTableRow() {
      if (!tableWrap) return;
      var levels = currentLevels();
      var level = levels[levelIndex];
      all(tableWrap, 'tbody tr').forEach(function (row) {
        row.classList.toggle('is-current', row.getAttribute('data-spp-row') === String(level));
      });
    }

    function renderReview() {
      var variant = currentVariant();
      if (!variant) return;

      var set = function (selector, value) {
        var node = root.querySelector(selector);
        if (node) node.textContent = value;
      };

      set('[data-spp-review-variant]', variant.title);
      set('[data-spp-review-quality]', variant.optionQuality || qualityLabel(variant.quality));
      set('[data-spp-review-package]', variant.optionPackage || groupDigits(variant.units) + ' units');
      set('[data-spp-review-unit]', unitPrice(shape, variant.price, variant.units));
      set('[data-spp-review-total]', variant.moneyLong || variant.money);

      var wantsLogo = logoValue() === 'yes';
      set('[data-spp-review-logo]', wantsLogo ? STRINGS.yes : STRINGS.no);

      var fileRow = root.querySelector('[data-spp-review-file-row]');
      if (fileRow) {
        var chosen = fileInput && fileInput.files && fileInput.files[0];
        fileRow.hidden = !(wantsLogo && chosen);
        if (wantsLogo && chosen) set('[data-spp-review-file]', chosen.name);
      }

      set('[data-spp-review-brand]', (brandInput && brandInput.value.trim()) || STRINGS.none);

      var freight = freightInputs.filter(function (input) {
        return input.checked;
      })[0];
      set('[data-spp-review-freight]', freight ? freight.value : STRINGS.none);

      var place = [];
      if (cityInput && cityInput.value.trim()) place.push(cityInput.value.trim());
      if (countryInput && countryInput.value.trim()) place.push(countryInput.value.trim());
      set('[data-spp-review-destination]', place.length ? place.join(', ') : STRINGS.none);
    }

    function render(focusTarget) {
      steps.forEach(function (step) {
        step.hidden = Number(step.getAttribute('data-spp-step')) !== current;
      });
      bars.forEach(function (bar) {
        var index = Number(bar.getAttribute('data-spp-bar'));
        bar.classList.toggle('is-done', index < current);
        bar.classList.toggle('is-now', index === current);
      });
      if (stepline) stepline.textContent = fill(STRINGS.stepOf, { SPP_STEP: current, SPP_TOTAL: STEP_COUNT });

      var active = steps[current - 1];
      var heading = active ? active.querySelector('h3') : null;
      if (heading && stepTitle) stepTitle.textContent = heading.textContent;

      if (prevBtn) prevBtn.hidden = current === 1;
      if (nextBtn) nextBtn.hidden = current === STEP_COUNT;
      if (submitBtn) submitBtn.hidden = current !== STEP_COUNT;

      if (body) body.scrollTop = 0;
      if (current === STEP_COUNT) renderReview();
      if (current !== STEP_COUNT) clearError(4);

      if (focusTarget && focusTarget.focus) {
        focusTarget.focus();
      } else if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
    }

    /* ------------------------------------------------------------------
       Errors
       ------------------------------------------------------------------ */
    function showError(step, message) {
      var node = root.querySelector('[data-spp-error="' + step + '"]');
      if (!node) return;
      node.textContent = message;
      node.hidden = false;
    }

    function clearError(step) {
      var node = root.querySelector('[data-spp-error="' + step + '"]');
      if (!node) return;
      node.textContent = '';
      node.hidden = true;
    }

    /* ------------------------------------------------------------------
       Logo
       ------------------------------------------------------------------ */
    function logoValue() {
      var picked = logoChoices.filter(function (input) {
        return input.checked;
      })[0];
      return picked ? picked.value : 'no';
    }

    function releasePreview() {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        previewUrl = null;
      }
    }

    function clearFile() {
      releasePreview();
      if (fileInput) fileInput.value = '';
      if (fileCard) fileCard.hidden = true;
      if (filePreview) filePreview.textContent = '';
      if (fileProgress) {
        fileProgress.hidden = true;
        var meter = fileProgress.querySelector('i');
        if (meter) meter.style.width = '0';
      }
    }

    function showFileError(message) {
      if (!fileError) return;
      fileError.textContent = message;
      fileError.hidden = false;
    }

    function clearFileError() {
      if (!fileError) return;
      fileError.textContent = '';
      fileError.hidden = true;
    }

    function acceptFile(file) {
      clearFileError();
      if (!file) return false;

      var ext = extensionOf(file.name);
      var typeOk = ALLOWED_EXT.indexOf(ext) > -1;
      /* The browser's MIME string is a hint, not proof — the extension has to
         agree with it, and both have to be on the list. */
      if (typeOk && file.type) {
        var mime = file.type.toLowerCase();
        var mimeOk =
          mime.indexOf('image/png') === 0 ||
          mime.indexOf('image/jpeg') === 0 ||
          mime.indexOf('image/jpg') === 0 ||
          mime.indexOf('image/svg') === 0 ||
          mime.indexOf('application/pdf') === 0;
        if (!mimeOk) typeOk = false;
      }
      if (!typeOk) {
        clearFile();
        showFileError(STRINGS.errFileType);
        return false;
      }
      if (file.size > (data.maxFileBytes || MAX_FILE_BYTES)) {
        clearFile();
        showFileError(STRINGS.errFileSize);
        return false;
      }

      if (fileName) fileName.textContent = file.name;
      if (fileSize) fileSize.textContent = formatBytes(file.size) + ' · ' + ext.toUpperCase();

      releasePreview();
      if (filePreview) {
        filePreview.textContent = '';
        if (PREVIEWABLE.indexOf(ext) > -1) {
          previewUrl = URL.createObjectURL(file);
          var image = document.createElement('img');
          image.src = previewUrl;
          image.alt = '';
          filePreview.appendChild(image);
        } else {
          var badge = document.createElement('span');
          badge.textContent = ext.toUpperCase();
          filePreview.appendChild(badge);
        }
      }

      if (fileCard) fileCard.hidden = false;
      clearError(3);
      return true;
    }

    function applyLogoChoice() {
      var wants = logoValue() === 'yes';
      if (uploadBlock) uploadBlock.hidden = !wants;
      if (fileInput) fileInput.disabled = !wants;
      if (propLogo) propLogo.value = wants ? STRINGS.yes : STRINGS.no;
      if (!wants) {
        clearFile();
        clearFileError();
      }
      clearError(3);
    }

    /* ------------------------------------------------------------------
       Validation
       ------------------------------------------------------------------ */
    function validate(step) {
      clearError(step);

      if (step === 1) {
        if (!quality) {
          showError(1, STRINGS.errQuality);
          return qualityInputs[0] || null;
        }
        return null;
      }

      if (step === 2) {
        var variant = currentVariant();
        if (!variant || !variant.available) {
          showError(2, STRINGS.errVariant);
          return stepper || null;
        }
        return null;
      }

      if (step === 3) {
        if (logoValue() === 'yes') {
          var chosen = fileInput && fileInput.files && fileInput.files[0];
          if (!chosen) {
            showFileError(STRINGS.errFile);
            return fileInput || null;
          }
        }
        if (ackInput && !ackInput.checked) {
          showError(3, STRINGS.errAck);
          return ackInput;
        }
        return null;
      }

      return null;
    }

    /* ------------------------------------------------------------------
       Open / close, focus trap
       ------------------------------------------------------------------ */
    function focusables() {
      return all(panel, 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])').filter(function (node) {
        return node.offsetParent !== null || node === document.activeElement;
      });
    }

    function open(trigger) {
      opener = trigger || null;
      root.hidden = false;
      /* Read a layout value so the browser commits the un-hidden state before
         the class lands — that gives the transform transition a starting
         point without depending on a frame callback that a background tab may
         never schedule. */
      void root.offsetWidth;
      root.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', onKeydown, true);
      current = 1;
      render();
    }

    function close() {
      root.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', onKeydown, true);
      window.setTimeout(function () {
        if (!root.classList.contains('is-open')) root.hidden = true;
      }, 340);
      if (opener && opener.focus) opener.focus();
    }

    function onKeydown(event) {
      if (root.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key === 'Tab') {
        var nodes = focusables();
        if (!nodes.length) return;
        var first = nodes[0];
        var last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
        return;
      }

      /* Arrow keys walk the package levels while the stepper has focus. */
      if (current === 2 && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
        if (!stepper || !stepper.contains(document.activeElement)) return;
        event.preventDefault();
        move(event.key === 'ArrowRight' ? 1 : -1);
      }
    }

    function move(delta) {
      var levels = currentLevels();
      var next = levelIndex + delta;
      if (next < 0 || next > levels.length - 1) return;
      levelIndex = next;
      renderStepper();
    }

    /* ------------------------------------------------------------------
       Cart
       ------------------------------------------------------------------ */
    function rootUrl() {
      var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || data.rootUrl || '/';
      return base.charAt(base.length - 1) === '/' ? base : base + '/';
    }

    function existingKeys() {
      return fetch(rootUrl() + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          return (cart.items || [])
            .filter(function (item) {
              return item.product_id === data.productId;
            })
            .map(function (item) {
              return item.key;
            });
        })
        .catch(function () {
          return [];
        });
    }

    /* One package per order line. An earlier Sushi Push Pop line is removed
       after the new one lands, so stepping from 5,000 to 10,000 replaces the
       package instead of quietly ordering both. */
    function dropKeys(keys) {
      if (!keys.length) return Promise.resolve();
      var updates = {};
      keys.forEach(function (key) {
        updates[key] = 0;
      });
      return fetch(rootUrl() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).catch(function () {});
    }

    function setBusy(state) {
      busy = state;
      if (!submitBtn) return;
      submitBtn.disabled = state;
      submitBtn.setAttribute('data-busy', state ? 'true' : 'false');
      if (submitLabel) {
        if (state) {
          submitLabel.setAttribute('data-idle', submitLabel.textContent);
          submitLabel.textContent = STRINGS.adding;
        } else if (submitLabel.getAttribute('data-idle')) {
          submitLabel.textContent = submitLabel.getAttribute('data-idle');
        }
      }
      if (fileProgress) {
        var wantsProgress = state && logoValue() === 'yes' && fileInput && fileInput.files.length > 0;
        fileProgress.hidden = !wantsProgress;
        if (!state) {
          var meter = fileProgress.querySelector('i');
          if (meter) meter.style.width = '0';
        }
      }
    }

    function post(formData) {
      return new Promise(function (resolve, reject) {
        var request = new XMLHttpRequest();
        request.open('POST', rootUrl() + 'cart/add.js', true);
        request.setRequestHeader('Accept', 'application/json');

        if (request.upload && fileProgress) {
          request.upload.onprogress = function (event) {
            if (!event.lengthComputable) return;
            var meter = fileProgress.querySelector('i');
            if (meter) meter.style.width = Math.round((event.loaded / event.total) * 100) + '%';
          };
        }

        request.onload = function () {
          var payload = null;
          try {
            payload = JSON.parse(request.responseText);
          } catch (error) {
            payload = null;
          }
          if (request.status >= 200 && request.status < 300) {
            resolve(payload);
            return;
          }
          var failure = new Error((payload && (payload.description || payload.message)) || STRINGS.errCart);
          failure.fromShopify = Boolean(payload && (payload.description || payload.message));
          reject(failure);
        };
        request.onerror = function () {
          reject(new Error(STRINGS.errCart));
        };
        request.send(formData);
      });
    }

    function submit() {
      if (busy) return;

      for (var step = 1; step <= STEP_COUNT; step++) {
        var bad = validate(step);
        if (bad) {
          current = step;
          render(bad);
          return;
        }
      }

      var variant = currentVariant();
      if (!variant) {
        showError(4, STRINGS.errVariant);
        return;
      }

      /* An empty text property would otherwise reach the order as a blank
         line. Disabled inputs are simply not submitted. */
      var optionals = [brandInput, countryInput, cityInput].filter(Boolean);
      optionals.forEach(function (input) {
        input.disabled = input.value.trim() === '';
      });

      setBusy(true);
      clearError(4);

      var payload = new FormData(form);
      payload.set('id', String(variant.id));
      payload.set('quantity', '1');
      /* The two radio groups only drive the drawer. Shopify has no use for
         them, so they do not travel to the cart. */
      payload.delete('spp-quality');
      payload.delete('spp-logo');

      optionals.forEach(function (input) {
        input.disabled = false;
      });

      existingKeys()
        .then(function (keys) {
          return post(payload).then(function () {
            return dropKeys(keys);
          });
        })
        .then(function () {
          window.location.href = data.afterAdd === 'cart' ? rootUrl() + 'cart' : rootUrl() + 'checkout';
        })
        .catch(function (error) {
          setBusy(false);
          showError(4, error && error.fromShopify ? error.message : STRINGS.errCart);
        });
    }

    /* ------------------------------------------------------------------
       Wiring
       ------------------------------------------------------------------ */
    qualityInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        quality = input.value;
        levelIndex = 0;
        clearError(1);
        renderStepper();
        buildTable();
      });
    });

    if (prevQty) {
      prevQty.addEventListener('click', function () {
        move(-1);
      });
    }
    if (nextQty) {
      nextQty.addEventListener('click', function () {
        move(1);
      });
    }

    if (tableToggle) {
      tableToggle.addEventListener('click', function () {
        var expanded = tableToggle.getAttribute('aria-expanded') === 'true';
        tableToggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (tableWrap) tableWrap.hidden = expanded;
        if (tableLabel) tableLabel.textContent = expanded ? STRINGS.viewList : STRINGS.hideList;
      });
    }

    logoChoices.forEach(function (input) {
      input.addEventListener('change', applyLogoChoice);
    });

    if (fileInput) {
      fileInput.addEventListener('change', function () {
        acceptFile(fileInput.files && fileInput.files[0]);
      });
    }
    if (fileReplace) {
      fileReplace.addEventListener('click', function () {
        if (fileInput) fileInput.click();
      });
    }
    if (fileRemove) {
      fileRemove.addEventListener('click', function () {
        clearFile();
        clearFileError();
        if (fileInput) fileInput.focus();
      });
    }

    if (dropZone && fileInput) {
      ['dragenter', 'dragover'].forEach(function (name) {
        dropZone.addEventListener(name, function (event) {
          event.preventDefault();
          dropZone.classList.add('is-dragging');
        });
      });
      ['dragleave', 'drop'].forEach(function (name) {
        dropZone.addEventListener(name, function (event) {
          event.preventDefault();
          dropZone.classList.remove('is-dragging');
        });
      });
      dropZone.addEventListener('drop', function (event) {
        var dropped = event.dataTransfer && event.dataTransfer.files;
        if (!dropped || !dropped.length) return;
        if (fileInput.disabled) return;
        /* DataTransfer is the only way to put a dropped file into a real
           <input type="file"> so the native form still carries it. */
        if (typeof DataTransfer !== 'function') {
          showFileError(STRINGS.errFileDrop);
          return;
        }
        var carrier = new DataTransfer();
        carrier.items.add(dropped[0]);
        fileInput.files = carrier.files;
        acceptFile(dropped[0]);
      });
    }

    if (ackInput) {
      ackInput.addEventListener('change', function () {
        if (ackInput.checked) clearError(3);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        var bad = validate(current);
        if (bad) {
          if (bad.focus) bad.focus();
          return;
        }
        if (current < STEP_COUNT) {
          current += 1;
          render();
        }
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        if (current > 1) {
          current -= 1;
          render();
        }
      });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      submit();
    });

    if (submitBtn) {
      submitBtn.addEventListener('click', function (event) {
        event.preventDefault();
        submit();
      });
    }

    all(root, '[data-spp-close]').forEach(function (button) {
      button.addEventListener('click', close);
    });
    if (scrim) scrim.addEventListener('click', close);

    /* Clicks inside the panel must never reach the scrim. */
    if (panel) {
      panel.addEventListener('click', function (event) {
        event.stopPropagation();
      });
    }

    applyLogoChoice();

    /* ------------------------------------------------------------------
       Triggers — the existing card keeps its href to the canonical product
       page, so without this script it stays a working link.
       ------------------------------------------------------------------ */
    all(document, '[data-spp-open]').forEach(function (trigger) {
      if (trigger.getAttribute('data-spp-bound') === 'true') return;
      trigger.setAttribute('data-spp-bound', 'true');
      trigger.addEventListener('click', function (event) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
        event.preventDefault();
        open(trigger);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
